import { useCallback, useEffect, useState } from 'react';
import { DATA_ADAPTER } from '../config/adapters';
import { isNativeCapacitorSync } from '../config/gapiProbes';
import { NETWORK_PATH_CHANGE_EVENT } from '../config/gapiReconnect';
import { RESULT_HISTORY_TODAY_LIMIT } from '../core/lottery/resultHistory';
import { isIosWebKit } from '../core/platform/iosWebKit';
import type {
  PlatformLotteryDraw,
  PlatformLotterySource,
} from '../services/lottery/platformLotteryDrawTypes';
import {
  mergePreferRicherDraws,
  readPlatformDrawsCache,
  writePlatformDrawsCache,
} from '../services/lottery/platformLotteryDrawsCache';

function isPlatformSource(source: string | null | undefined): source is PlatformLotterySource {
  return source === 'florida' || source === 'newyork';
}

function startPlatformDrawsFeed(
  src: PlatformLotterySource,
  limitCount: number,
  apply: (rows: PlatformLotteryDraw[]) => void,
  onFail: (message: string) => void,
  onUnarySettled: () => void,
): () => void {
  let cancelled = false;
  let listenPainted = false;
  let unsubscribe: (() => void) | undefined;
  void import('../services/lottery/subscribePlatformLotteryDraws')
    .then(async ({ fetchRecentPlatformLotteryDraws, subscribeRecentPlatformLotteryDraws }) => {
      if (cancelled) return;
      const paint = (rows: PlatformLotteryDraw[]) => {
        if (cancelled || rows.length === 0) return;
        apply(rows);
      };
      const hoy = fetchRecentPlatformLotteryDraws(src, RESULT_HISTORY_TODAY_LIMIT)
        .then((rows) => {
          if (!listenPainted) paint(rows);
        })
        .catch(() => undefined);
      // Week cap via unary *before* Listen — iOS Safari WebChannel often hangs
      // onSnapshot and then blocks later getDocs on the same client.
      const week = fetchRecentPlatformLotteryDraws(src, limitCount)
        .then((rows) => {
          if (!listenPainted) paint(rows);
        })
        .catch(() => undefined);
      await Promise.allSettled([hoy, week]);
      if (cancelled) return;
      onUnarySettled();
      // iOS WebKit + Capacitor WebView: Listen/WebChannel can hang the shared
      // client after unary. Retry via `online` / network-path events instead.
      if (isIosWebKit() || isNativeCapacitorSync()) return;
      unsubscribe = subscribeRecentPlatformLotteryDraws(
        src,
        limitCount,
        (rows) => {
          if (rows.length > RESULT_HISTORY_TODAY_LIMIT) listenPainted = true;
          paint(rows);
        },
        (err) => {
          if (!cancelled) onFail(err.message || 'firestore-listen-failed');
        },
      );
      if (cancelled) unsubscribe();
    })
    .catch((err: unknown) => {
      if (cancelled) return;
      onFail(err instanceof Error ? err.message : 'firestore-listen-failed');
    });
  return () => {
    cancelled = true;
    unsubscribe?.();
  };
}

/**
 * Shared public lottery draws (same numbers for every house).
 * Hydrates last-good session cache, unary-fetches Hoy then the week cap,
 * then listens (Listen starts after unary so Safari WebChannel cannot stall it).
 */
export function useRecentPlatformLotteryDraws(
  source: string | null | undefined,
  limitCount = 16,
) {
  const [draws, setDraws] = useState<PlatformLotteryDraw[]>(() => (
    isPlatformSource(source) ? readPlatformDrawsCache(source) : []
  ));
  const [loading, setLoading] = useState(
    () => DATA_ADAPTER === 'firebase' && isPlatformSource(source),
  );
  const [error, setError] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const retry = useCallback(() => setRetryKey((n) => n + 1), []);

  useEffect(() => {
    let timer = 0;
    const bump = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => setRetryKey((n) => n + 1), 400);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') bump();
    };
    window.addEventListener('online', bump);
    window.addEventListener(NETWORK_PATH_CHANGE_EVENT, bump);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('online', bump);
      window.removeEventListener(NETWORK_PATH_CHANGE_EVENT, bump);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);

  useEffect(() => {
    if (DATA_ADAPTER !== 'firebase' || !isPlatformSource(source)) {
      setDraws([]);
      setError(null);
      setLoading(false);
      return undefined;
    }
    const src: PlatformLotterySource = source;
    const cached = readPlatformDrawsCache(src);
    setDraws((prev) => (cached.length ? cached : (prev[0]?.source === src ? prev : [])));
    setLoading(true);
    setError(null);
    return startPlatformDrawsFeed(
      src,
      limitCount,
      (rows) => {
        setDraws((prev) => {
          const merged = mergePreferRicherDraws(prev, rows);
          writePlatformDrawsCache(src, merged);
          return merged;
        });
        setError(null);
      },
      (message) => {
        setError(message);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, [source, limitCount, retryKey]);

  return { draws, error: draws.length ? null : error, loading, retry };
}
